/**
 * Canonical tier resolution logic - single source of truth for the entire monorepo.
 *
 * Previously split across:
 *   frontend: grandfathering.js · featureGate.js · accountProduct.js
 *   backend:  landlord-tier.service.ts
 *
 * Both workspaces now import from here. No framework dependencies.
 */

// ── Types ─────────────────────────────────────────────────────────────────────

/**
 * Canonical tier keys used throughout the app.
 *
 * - `starter` / `growth` / `scale`: self-serve Stripe tiers (market_2026).
 * - `enterprise`: custom-sold tier ($499) - higher door allowance; sales-led.
 */
export type TierKey = "starter" | "growth" | "scale" | "enterprise";

/**
 * Canonical per-tier unit caps (market 2026 Value-Capture: Starter 15 · Growth 50 · Scale 150).
 * Every tier includes the full LandlordOps OS; caps are the only differentiator.
 * Grandfathered Scale Suite users bypass these via `getUnitCapForTier("grandfathered")`.
 */
export const UNIT_CAPS: Record<TierKey, number> = {
  starter: 15,
  growth: 50,
  scale: 150,
  enterprise: 250,
};

/**
 * Resolve the portfolio unit cap for a normalized tier key.
 *
 * Pass `"grandfathered"` for users who hold legacy Scale Suite entitlements - * they are uncapped so existing portfolios are never frozen by a pricing
 * migration.
 *
 * @example
 *   const tier = isGrandfatheredScaleSuite(user) ? "grandfathered" : resolveUserTier(user);
 *   const cap = getUnitCapForTier(tier);
 *   if (currentUnits >= cap) showUpgradeModal();
 */
export function getUnitCapForTier(tier: TierKey | "grandfathered"): number {
  if (tier === "grandfathered") return Number.POSITIVE_INFINITY;
  return UNIT_CAPS[tier] ?? UNIT_CAPS.starter;
}

// ── Plan key helpers ──────────────────────────────────────────────────────────

/**
 * True for legacy messaging-only Stripe SKUs (`chat_only`, `chat_starter`,
 * `chat_growth`, `chat_scale`). These now resolve to the same door bands and
 * full OS entitlements as the matching suite keys.
 */
export function isOpsChatStylePlanKey(plan: unknown): boolean {
  const p = String(plan ?? "")
    .trim()
    .toLowerCase();
  return p === "chat_only" || p.startsWith("chat_");
}

/**
 * Extract the raw plan/subscription string from a Firestore user or landlord
 * document. Handles all field aliases in order of precedence:
 *   subscription > selectedPlan > plan.tier > plan (string)
 */
export function extractRawPlanKey(userData: Record<string, unknown>): string {
  if (!userData || typeof userData !== "object") return "";
  const planField = userData.plan;
  const tierFromNested =
    planField && typeof planField === "object" && planField !== null
      ? ((planField as Record<string, unknown>).tier as string | undefined)
      : undefined;
  const raw =
    userData.subscription ??
    userData.selectedPlan ??
    tierFromNested ??
    (typeof planField === "string" ? planField : null) ??
    "";
  return String(raw).trim().toLowerCase();
}

/**
 * Normalize a raw Firestore subscription key to a canonical 3-tier key.
 *
 * - `market_2026` (default): starter · growth · scale + chat_* variants
 * - `legacy_2025`:           starter · pro · growth · (business → scale)
 *
 * Suite `business` (legacy catalog and pre-OpsChat-groups Firestore values) is
 * treated as **Scale** for door caps. OpsChat `chat_business` stays on the
 * Growth door band (same as `chat_growth`). All bands include the full OS.
 */
export function normalizeTierKey(
  rawPlan: string,
  pricingTierGroup: string = "market_2026"
): TierKey {
  const s = String(rawPlan ?? "starter")
    .trim()
    .toLowerCase();

  // Enterprise is valid in both pricing groups - custom-sold, above Scale.
  if (s === "enterprise") return "enterprise";

  // Legacy suite "Business" self-serve tier → same door band as Scale.
  if (s === "business") return "scale";

  if (pricingTierGroup === "legacy_2025") {
    if (s === "scale" || s === "chat_scale") return "scale";
    if (
      s === "growth" ||
      s === "chat_growth" ||
      s === "pro" ||
      s === "chat_business" ||
      s === "chat_pro"
    ) {
      return "growth";
    }
    return "starter";
  }

  // market_2026 (default)
  if (s === "scale" || s === "chat_scale") return "scale";
  if (
    s === "growth" ||
    s === "chat_growth" ||
    s === "pro" ||
    s === "chat_business" ||
    s === "chat_pro"
  ) {
    return "growth";
  }
  return "starter";
}

// ── Grandfathering ────────────────────────────────────────────────────────────

/**
 * For Scale OS grandfathering: allow `past_due` so users can reach
 * Payments/billing; block `canceled`, `unpaid`, `inactive`.
 */
export function subscriptionAllowsGrandfatheredScale(
  userData: Record<string, unknown>
): boolean {
  const st = String(userData?.subscriptionStatus ?? "").toLowerCase();
  if (
    st === "canceled" ||
    st === "cancelled" ||
    st === "unpaid" ||
    st === "inactive"
  ) {
    return false;
  }
  if (st === "active" || st === "trialing" || st === "past_due") return true;
  const plan = extractRawPlanKey(userData);
  if (!plan || isOpsChatStylePlanKey(plan)) return false;
  return true;
}

/**
 * When true, treat the user as Scale tier for door caps, Rental Command
 * Center, and emergency escalation while Stripe stays on their current plan.
 *
 * Canonical replacement for:
 *   - frontend: `grandfathering.isGrandfatheredScaleSuite`
 *   - backend:  `landlord-tier.service.isGrandfatheredScaleSuiteUser`
 */
export function isGrandfatheredScaleSuite(
  userData: Record<string, unknown> | null | undefined
): boolean {
  if (!userData || typeof userData !== "object") return false;
  if (userData.grandfatheredScaleSuite === true) return true;
  const plan = extractRawPlanKey(userData);
  if (!plan || isOpsChatStylePlanKey(plan)) return false;
  return subscriptionAllowsGrandfatheredScale(userData);
}

// ── Tier resolution ───────────────────────────────────────────────────────────

/**
 * Resolve a Firestore user document to a normalized tier key.
 * Grandfathered users always resolve to `"scale"` regardless of their Stripe
 * plan. Pass `pricingTierGroupOverride` to force a pricing group rather than
 * reading it from the user document.
 */
export function resolveUserTier(
  userData: Record<string, unknown> | null | undefined,
  pricingTierGroupOverride?: string
): TierKey {
  if (!userData) return "starter";
  if (isGrandfatheredScaleSuite(userData)) return "scale";
  const raw = extractRawPlanKey(userData);
  const group =
    pricingTierGroupOverride ??
    String(userData.pricingTierGroup ?? "market_2026");
  return normalizeTierKey(raw || "starter", group);
}
